<template>
  <div class="mt-6">
    <div class="text-sm lg:text-xl font-bold mb-4">
      Explore {{ daoType }}s on {{ daoChain }}
    </div>
    <div
      class="bg-white-005 p-4 rounded-2xl flex flex-col lg:flex-row"
      v-if="!loading"
    >
      <div
        class="
          overflow-x-auto
          mr-0
          lg:mr-6
          space-x-2 space-y-0
          lg:space-x-0 lg:space-y-4
          flex flex-row
          lg:flex-col
          mb-1
          lg:mb-0
        "
      >
        <MultiInfoSwitch
          @change="handleMultiSwitchUpdate"
          :options="tabs"
          :selectedIndex="state.selected"
        />
      </div>
      <div class="flex-1 min-w-0">
        <Dao v-if="state.selected == 0"></Dao>
        <Treasury v-if="state.selected == 1"></Treasury>
        <Members v-if="state.selected == 2"></Members>
        <Proposal v-if="state.selected == 3"></Proposal>
      </div>
    </div>
    <Skeletor v-else class="rounded" style="height: 442px"></Skeletor>
  </div>
</template>

<script setup>
import { queryHomeInfo } from '@/api/dao';
import MultiInfoSwitch from '@/components/Inputs/Switches/MultiInfoSwitch.vue';
import { useReqByBool } from '@/hooks';
import { formatVal, suffixNum } from '@/utils';
import { computed, inject, onMounted, reactive, watch } from 'vue';
import { Skeletor } from 'vue-skeletor';
import { homeChains, homeTypes } from '../../constant';
import Dao from './dao.vue';
import Members from './members.vue';
import Proposal from './proposal.vue';
import Treasury from './treasury.vue';

const homeState = inject('homeState');

const state = reactive({
  selected: 1,
  info: {},
});
const handleMultiSwitchUpdate = (index) => {
  state.selected = index;
};
const daoType = computed(() => homeTypes[homeState.selectedType].name);
const daoChain = computed(() => homeChains[homeState.selectedChain].name);

const tabs = computed(() => [
  {
    name: 'DAOs',
    timeSpan: '24H',
    value: state.info.totalDao,
    num: state.info.daoCountBy24h,
  },
  {
    name: 'Treasury',
    timeSpan: '24H',
    value: formatVal(state.info.totalTreasury, { bit: 2, omit: true }),
    valueSymbol: '$',
    rate: (state.info.treasuryRate * 100).toFixed(2),
    rateSymbol: '%',
  },
  {
    name: 'Members',
    timeSpan: '24H',
    subtitle: '(Governance token holders)',
    value: formatVal(state.info.governanceTokenMembers, { omit: true }),
    rate: (state.info.governanceTokenMembersRate * 100).toFixed(2),
    rateSymbol: '%',
  },
  {
    name: 'Proposal and votes',
    timeSpan: '24H',
    value: [
      {
        name: 'Proposal:',
        value: formatVal(state.info.totalProposal, { bit: 2, omit: true }),
        rate: state.info.proposalBy24h,
        iconClass: 'w-3 h-0.5 bg-icons-blue',
      },
      {
        name: 'Voters:',
        value: formatVal(state.info.totalVoters, { bit: 2, omit: true }),
        rate: state.info.votersBy24h,
        iconClass: 'w-3 h-3 rounded bg-icons-pink',
      },
      {
        name: 'Votes:',
        value: formatVal(state.info.totalVotes, { bit: 2, omit: true }),
        rate: state.info.votesBy24h,
        iconClass: 'w-3 h-3 rounded bg-icons-purple',
      },
    ],
  },
]);

const { loadData, loading } = useReqByBool(async () => {
  const res = await queryHomeInfo(
    homeTypes[homeState.selectedType].value,
    homeChains[homeState.selectedChain].value,
    homeState.filterStatus,
  );
  state.info = res;
});

watch(
  () => [
    homeState.selectedType,
    homeState.selectedChain,
    homeState.filterStatus,
  ],
  async () => {
    await loadData();
    state.selected = 1;
  },
);

onMounted(() => {
  loadData();
});
</script>

<style lang="scss" scoped></style>
